<template>
  <div class="page-modal">
    <widgetModal 
      title="Merge Accounts" 
      subtitle="Merge selected accounts into one account. Please set the name for the merged account."
      btnlefttext="Merge Accounts" 
      btncentertext="Cancel"
      :leftloading="merging_loading" 
      :centerloading="false" 
      :hiderightbtn="true"
      :footerhidden="false" 
      footer_padding="10px 18px 30px 30px"
      header_padding="30px 0px 0px 31px" 
      body_padding="0px 30px" 
      width="500px" 
      @onLeftClick="onConfirm" 
      @onCenterClick="onCancel">
      
      <div class="widget-modal-body-noscoll">
        <!-- Accounts to be merged -->
        <div class="merge-info-section">
          <h4 class="section-title">Accounts to be merged ({{ selectedAccounts.length }})</h4>
          <div class="accounts-list">
            <div 
              v-for="(account, index) in selectedAccounts" 
              :key="account.id" 
              class="account-item"
            >
              <widgetProfile 
                :title="account.name" 
                :subtitle="`Status: ${account.status || 'Unknown'} | Category: ${account.category || 'None'}`" 
                file_name="" 
                path=""
              ></widgetProfile>
            </div>
          </div>
        </div>

        <!-- Merged account name -->
        <div class="row">
          <widgetInputWrapper title="Merged Account Name" required>
            <widgetInput 
              v-model="merge_form.name" 
              type="text" 
              placeholder="Enter name for the merged account"
              :hasError="validation_errors.name"
            ></widgetInput>
            <div v-if="validation_errors.name" class="input-error">
              {{ validation_errors.name }}
            </div>
          </widgetInputWrapper>
        </div>

        <!-- Merged account settings -->
        <div class="row">
          <widgetInputWrapper title="Status">
            <widgetSelect 
              v-model="merge_form.status" 
              :opt="[
                { label: 'Active', value: 'active' },
                { label: 'Passive', value: 'passive' },
              ]" 
              placeholder="Account status"
            ></widgetSelect>
          </widgetInputWrapper>
          <div class="h-spacer"></div>
          <widgetInputWrapper title="Category">
            <widgetSelect 
              v-model="merge_form.category" 
              :opt="[
                { label: 'No Category', value: 'no_category' },
                { label: 'Gold', value: 'gold' },
                { label: 'Silver', value: 'silver' },
                { label: 'Bronze', value: 'bronze' }
              ]" 
              placeholder="No category"
            ></widgetSelect>
          </widgetInputWrapper>
        </div>

        <!-- Warning message -->
        <div class="warning-section">
          <div class="warning-box">
            <img src="@/assets/notification-icon/warning.svg" class="warning-icon" />
            <div class="warning-text">
              <strong>Warning:</strong> This action cannot be undone. All selected accounts will be merged into one account with the information above. Contacts, activities, and other data from all accounts will be combined.
            </div>
          </div>
        </div>
      </div>
    </widgetModal>
  </div>
</template>

<script>
import { ACCOUNT_MERGE } from "@/Store/Actions/accounts.js";

export default {
  name: "MergeAccountsModal",
  props: {
    selectedAccounts: {
      type: Array,
      required: true,
      default: () => []
    }
  },
  data() {
    return {
      merging_loading: false,
      merge_form: {
        name: "",
        status: "active",
        category: ""
      },
      validation_errors: {}
    };
  },
  mounted() {
    this.generateDefaultName();
  },
  methods: {
    generateDefaultName() {
      if (this.selectedAccounts.length > 0) {
        // Generate a default name from the first account
        const firstAccount = this.selectedAccounts[0];
        this.merge_form.name = `${firstAccount.name} (Merged)`;
      }
    },

    validateForm() {
      this.validation_errors = {};
      let isValid = true;

      // Validate account name
      if (!this.merge_form.name || this.merge_form.name.trim() === "") {
        this.validation_errors.name = "Merged account name is required";
        isValid = false;
      } else if (this.merge_form.name.trim().length < 3) {
        this.validation_errors.name = "Account name must be at least 3 characters";
        isValid = false;
      }

      return isValid;
    },

    async onConfirm() {
      if (!this.validateForm()) {
        return;
      }

      if (this.selectedAccounts.length < 2) {
        this.$notify({
          group: "status",
          title: "Error",
          text: "Please select at least 2 accounts to merge",
          type: "error",
          duration: 3000,
        });
        return;
      }

      this.merging_loading = true;

      try {
        const payload = {
          name: this.merge_form.name.trim(),
          status: this.merge_form.status,
          category: this.merge_form.category,
          account_ids: this.selectedAccounts.map(account => account.id)
        };

        const response = await this.$store.dispatch(ACCOUNT_MERGE, payload);

        if (response.data) {
          this.$notify({
            group: "status",
            title: "Success",
            text: `${this.selectedAccounts.length} accounts have been successfully merged`,
            type: "success",
            duration: 3000,
          });

          this.$emit("success", response.data);
        } else {
          throw new Error("Invalid response from server");
        }
      } catch (error) {
        console.error("Error merging accounts:", error);
        this.$notify({
          group: "status",
          title: "Error",
          text: "Failed to merge accounts. Please try again.",
          type: "error",
          duration: 3000,
        });
      } finally {
        this.merging_loading = false;
      }
    },

    onCancel() {
      this.$emit("close");
    }
  }
};
</script>

<style lang="scss" scoped>
.widget-modal-body-noscoll {
  .merge-info-section {
    margin-bottom: 20px;
    
    .section-title {
      font-size: 14px;
      font-weight: 600;
      color: #495057;
      margin-bottom: 12px;
      margin-top: 0;
    }

    .accounts-list {
      background: #f8f9fa;
      border-radius: 6px;
      padding: 12px;
      max-height: 200px;
      overflow-y: auto;

      .account-item {
        padding: 8px 0;
        border-bottom: 1px solid #dee2e6;

        &:last-child {
          border-bottom: none;
        }
      }
    }
  }

  .warning-section {
    margin-top: 20px;

    .warning-box {
      display: flex;
      align-items: flex-start;
      background: #fff3cd;
      border: 1px solid #ffeaa7;
      border-radius: 6px;
      padding: 12px;

      .warning-icon {
        width: 20px;
        height: 20px;
        margin-right: 10px;
        margin-top: 2px;
      }

      .warning-text {
        font-size: 13px;
        color: #856404;
        line-height: 1.4;

        strong {
          font-weight: 600;
        }
      }
    }
  }

  .input-error {
    color: #dc3545;
    font-size: 12px;
    margin-top: 4px;
  }

  .row {
    margin-bottom: 15px;
  }
}
</style>
